import { collectShapeIdsDeep } from '../../Core/UmlCanvasEngineHelper.js';
import { ShapeGeoBuilder } from '@jxUml';

/**
 * Builds a reusable, serializable package template from a live UmlPackageShape:
 * the package itself (with all nested children, recursive), plus any Icon/Comment
 * satellite shape directly linked to a package child (via a connector, or via
 * UmlCommentShape.linkedShapeId), plus every connector fully contained within
 * that set (internal package connectors + package-to-satellite connectors).
 * A satellite that is itself a Package or a loose Class is never pulled in —
 * only Icon/Comment, and only one hop out.
 */
class UmlPkgLibBuilder {
    /**
     * @param {object} packageShape - the selected UmlPackageShape (live instance, with children)
     * @param {object[]} allShapes - top-level shapes (umlCanvasEngine.shapes)
     * @param {object[]} allConnectors - all connectors (umlConnectorEngine.connectors)
     * @returns {{package: object, satellites: object[], connectors: object[]}}
     */
    static build(packageShape, allShapes, allConnectors) {
        const descendantIds = new Set(collectShapeIdsDeep(packageShape));

        const satellites = allShapes.filter((shape) => {
            if (descendantIds.has(shape.id)) return false; // it's already part of the package itself
            if (shape.kind !== 'icon' && shape.kind !== 'comment') return false;
            const linkedViaComment = shape.kind === 'comment' && descendantIds.has(shape.linkedShapeId);
            const linkedViaConnector = allConnectors.some(
                (connector) =>
                    (connector.fromShapeId === shape.id && descendantIds.has(connector.toShapeId)) ||
                    (connector.toShapeId === shape.id && descendantIds.has(connector.fromShapeId)),
            );
            return linkedViaComment || linkedViaConnector;
        });
        const satelliteIds = new Set(satellites.map((shape) => shape.id));

        const includedConnectors = allConnectors.filter((connector) => {
            const fromIncluded = descendantIds.has(connector.fromShapeId) || satelliteIds.has(connector.fromShapeId);
            const toIncluded = descendantIds.has(connector.toShapeId) || satelliteIds.has(connector.toShapeId);
            return fromIncluded && toIncluded;
        });

        // Plain-data clone (JSON round-trip) — same approach as UmlCanvasEngine.toSchema(),
        // strips class identity so this is safe to store as note body / reload later via
        // UmlShapeSerializer, and safe to mutate freely in UmlPkgLibInserter without touching
        // the live engine state.
        return JSON.parse(
            JSON.stringify({
                package: packageShape,
                satellites,
                connectors: includedConnectors,
            }),
        );
    }

    /**
     * Bounding box over the package shape and its satellites — used to scale/
     * position a capture-clone within a fixed-size preview area (see
     * computeFitTransform, UI/UmlCanvas/UmlPreviewDlgHelpers.js). Connector
     * waypoints are not considered; shape bounds are sufficient for this
     * preview's purpose.
     * @param {object} packageShape
     * @param {object[]} satellites
     * @returns {{minX: number, minY: number, width: number, height: number}}
     */
    static getPreviewBounds(packageShape, satellites) {
        const shapes = [packageShape, ...satellites];
        const minX = Math.min(...shapes.map((shape) => shape.x));
        const minY = Math.min(...shapes.map((shape) => shape.y));
        const maxX = Math.max(...shapes.map((shape) => shape.x + shape.w));
        const maxY = Math.max(...shapes.map((shape) => shape.y + ShapeGeoBuilder.effectiveHeight(shape)));
        return { minX, minY, width: maxX - minX, height: maxY - minY };
    }
}

export default UmlPkgLibBuilder;
